import {
  EMPLOYER_CHANGE_RESTRICTION_PERIOD_LABEL,
  EXAM_GRADE_1YEAR_LABEL,
  EXAM_GRADE_FINAL_LABEL,
  TMasterCompanyMainSkill,
} from "@/api/entities/master-company-main-skill.entity";
import { TMasterCompanySpecificBusinessClassification } from "@/api/entities/master-company-specific-business-classification.entity";
import { TGetCompanySpecificIndustryListOutputDto } from "@/api/services/global/master/dtos/companySpecificIndustry/get-master-company-specific-industry-list.dto";
import { IconOrderTable } from "@/assets/icons/IconOrderTable";
import { Button } from "@/components/elements/Button";
import { Switch } from "@/components/elements/Switch";
import { DATE_TIME_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { EllipsisText } from "@/features/master-data/components/CompanySpecificIndustry/EllipsisText";
import {
  canSetEmploymentTraining,
  canSetSpecificSkill,
  getEmploymentTrainingKey,
  getSpecificSkillKey,
} from "@/features/master-data/hooks/useCompanySpecificIndustryVisaFlags";
import { background, blue, button, divider, text } from "@/theme/colors";
import { dayjs } from "@/utils/dayjs";
import { toNumber } from "@/utils/number";
import { Box, Checkbox as MuiCheckbox, styled, TableCell, TableRow, Typography } from "@mui/material";
import { FC, memo, ReactNode, useMemo } from "react";
import { DraggableProvided } from "react-beautiful-dnd";

const TableCheckbox = styled(MuiCheckbox)`
  width: 18px;
  height: 18px;
  color: ${button.primary}!important;
  &.Mui-disabled {
    color: ${button.disable}!important;
  }
`;

// A classification row is as tall as its main-skill list so every column stays on the same grid.
const SUB_ROW_HEIGHT = 40;

const getClassificationHeight = (mainSkills: TMasterCompanyMainSkill[]) => Math.max(mainSkills.length, 1) * SUB_ROW_HEIGHT;

// A main skill edit is shown on its classification box, so take the latest of the two timestamps.
const getLatestUpdatedAt = (classification: TMasterCompanySpecificBusinessClassification) =>
  [classification.updatedAt, ...classification.mainSkills.map((mainSkill) => mainSkill.updatedAt)]
    .filter(Boolean)
    .sort((a, b) => dayjs(b).valueOf() - dayjs(a).valueOf())[0];

// Just wide enough for a checkbox; the header label wraps onto two lines.
export const CHECKBOX_COLUMN_WIDTH = "56px";

// Sized to their header labels (16px bold, full-width chars) plus the 8px header padding on both sides.
const INDUSTRY_COLUMN_WIDTH = "176px";
const CLASSIFICATION_COLUMN_WIDTH = "192px";
const SECTOR_COUNCIL_COLUMN_WIDTH = "208px";

const SUB_ROW_BORDER = `1px solid ${divider.middle}`;

// Only ellipsizes when the text has a bounded width (`maxWidth`); otherwise the cell grows to fit the whole text.
const ELLIPSIS_TEXT_SX = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
} as const;

// Each value occupies exactly one slot; a long value is ellipsized instead of wrapping into the next one.
const SUB_ROW_TEXT_SX = {
  ...ELLIPSIS_TEXT_SX,
  lineHeight: `${SUB_ROW_HEIGHT}px`,
  height: `${SUB_ROW_HEIGHT}px`,
} as const;

// Main skills stack inside one classification box, so their separator is drawn per item rather than by the table.
const MAIN_SKILL_ROW_SX = {
  ...SUB_ROW_TEXT_SX,
  paddingLeft: "4px",
  "&:not(:last-of-type)": { borderBottom: SUB_ROW_BORDER },
} as const;

// Renders one entry per main skill, keeping the classification grid so the row lines up with the other columns.
// Text columns pass `getValue`; columns with their own control (e.g. a checkbox) pass `renderValue`.
const MainSkillCell: FC<{
  classifications: TMasterCompanySpecificBusinessClassification[];
  width: string;
  align?: "left" | "center";
  getValue?: (_mainSkill: TMasterCompanyMainSkill) => string;
  renderValue?: (_mainSkill: TMasterCompanyMainSkill, _classification: TMasterCompanySpecificBusinessClassification) => ReactNode;
  renderEmpty?: (_classification: TMasterCompanySpecificBusinessClassification) => ReactNode;
}> = ({ classifications, width, align = "left", getValue, renderValue, renderEmpty }) => {
  // A control (e.g. a checkbox) sits in the middle of its slot, horizontally too when the column is centered.
  const controlSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: align === "center" ? "center" : "flex-start",
    ...(align === "center" && { paddingLeft: 0 }),
  } as const;

  return (
    <TableCell sx={{ minWidth: width, textAlign: `${align} !important` }}>
      {classifications.length > 0
        ? classifications.map((classification, index, defaultArray) => (
            <Box
              sx={{
                height: `${getClassificationHeight(classification.mainSkills)}px`,
                maxWidth: width,
                borderBottom: index !== defaultArray.length - 1 ? SUB_ROW_BORDER : "none",
              }}
              key={classification.id}
            >
              {classification.mainSkills.length > 0 ? (
                classification.mainSkills.map((mainSkill) =>
                  renderValue ? (
                    <Box sx={{ ...MAIN_SKILL_ROW_SX, ...controlSx }} key={mainSkill.id}>
                      {renderValue(mainSkill, classification)}
                    </Box>
                  ) : (
                    <EllipsisText
                      value={getValue?.(mainSkill)}
                      emptyText={EMPTY_STR.TEXT}
                      sx={{ ...MAIN_SKILL_ROW_SX }}
                      color={classification.isHidden || mainSkill.isHidden ? text.tertiary : text.primary}
                      key={mainSkill.id}
                    />
                  ),
                )
              ) : renderEmpty ? (
                <Box sx={{ ...SUB_ROW_TEXT_SX, paddingLeft: "4px", ...controlSx }}>{renderEmpty(classification)}</Box>
              ) : (
                <Typography component="p" sx={{ ...SUB_ROW_TEXT_SX, paddingLeft: "4px" }} color={text.tertiary} variant="body14">
                  {EMPTY_STR.TEXT}
                </Typography>
              )}
            </Box>
          ))
        : EMPTY_STR.TEXT}
    </TableCell>
  );
};

const ClassificationCell: FC<{
  classifications: TMasterCompanySpecificBusinessClassification[];
  width: string;
  renderValue: (_classification: TMasterCompanySpecificBusinessClassification) => ReactNode;
}> = ({ classifications, width, renderValue }) => (
  <TableCell sx={{ minWidth: width, textAlign: "center !important" }}>
    {classifications.length > 0
      ? classifications.map((classification, index, defaultArray) => (
          <Box
            sx={{
              height: `${getClassificationHeight(classification.mainSkills)}px`,
              borderBottom: index !== defaultArray.length - 1 ? SUB_ROW_BORDER : "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            key={classification.id}
          >
            {renderValue(classification)}
          </Box>
        ))
      : EMPTY_STR.TEXT}
  </TableCell>
);

type TCompanySpecificIndustryRowProps = {
  row: TGetCompanySpecificIndustryListOutputDto;
  provided: DraggableProvided;
  togglingKey: string;
  onToggleSpecificSkill: (_industryId: number, _classification: TMasterCompanySpecificBusinessClassification) => void;
  onToggleEmploymentTraining: (_industryId: number, _classificationId: number, _mainSkill: TMasterCompanyMainSkill) => void;
  onChangeHidden: (_row: TGetCompanySpecificIndustryListOutputDto) => void;
  onEdit: (_row: TGetCompanySpecificIndustryListOutputDto) => void;
};

const isSameStyle = (a: object | undefined, b: object | undefined) => {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  const keysA = Object.keys(a);
  return keysA.length === Object.keys(b).length && keysA.every((key) => a[key as keyof typeof a] === b[key as keyof typeof b]);
};

const areRowPropsEqual = (prev: TCompanySpecificIndustryRowProps, next: TCompanySpecificIndustryRowProps) =>
  prev.row === next.row &&
  prev.togglingKey === next.togglingKey &&
  prev.onToggleSpecificSkill === next.onToggleSpecificSkill &&
  prev.onToggleEmploymentTraining === next.onToggleEmploymentTraining &&
  prev.onChangeHidden === next.onChangeHidden &&
  prev.onEdit === next.onEdit &&
  prev.provided.innerRef === next.provided.innerRef &&
  isSameStyle(prev.provided.draggableProps.style, next.provided.draggableProps.style) &&
  isSameStyle(prev.provided.dragHandleProps ?? undefined, next.provided.dragHandleProps ?? undefined);

const CompanySpecificIndustryRowBase: FC<TCompanySpecificIndustryRowProps> = ({
  row,
  provided,
  togglingKey,
  onToggleSpecificSkill,
  onToggleEmploymentTraining,
  onChangeHidden,
  onEdit,
}) => {
  const toggling = useMemo(() => new Set(togglingKey ? togglingKey.split(",") : []), [togglingKey]);

  return (
    <TableRow ref={provided.innerRef} {...provided.draggableProps}>
      <TableCell sx={{ minWidth: "40px", paddingLeft: "10px !important" }} {...provided.dragHandleProps}>
        <IconOrderTable color={blue[80]} />
      </TableCell>
      <ClassificationCell
        classifications={row.companySpecificBusinessClassifications}
        width={CHECKBOX_COLUMN_WIDTH}
        renderValue={(classification) => (
          <TableCheckbox
            checked={classification.isSpecificSkill}
            disabled={!canSetSpecificSkill(row, classification) || toggling.has(getSpecificSkillKey(toNumber(classification.id)))}
            onChange={() => onToggleSpecificSkill(toNumber(row.id), classification)}
          />
        )}
      />
      <MainSkillCell
        classifications={row.companySpecificBusinessClassifications}
        width={CHECKBOX_COLUMN_WIDTH}
        align="center"
        renderValue={(mainSkill, classification) => (
          <TableCheckbox
            checked={mainSkill.isEmploymentTraining}
            disabled={!canSetEmploymentTraining(row, classification, mainSkill) || toggling.has(getEmploymentTrainingKey(toNumber(mainSkill.id)))}
            onChange={() => onToggleEmploymentTraining(toNumber(row.id), toNumber(classification.id), mainSkill)}
          />
        )}
        renderEmpty={() => <TableCheckbox checked={false} disabled />}
      />
      <TableCell sx={{ minWidth: INDUSTRY_COLUMN_WIDTH, paddingLeft: "8px !important" }}>
        <EllipsisText
          value={row.companySpecificIndustry}
          emptyText={EMPTY_STR.TEXT}
          sx={{ maxWidth: INDUSTRY_COLUMN_WIDTH }}
          color={row.isHidden ? text.tertiary : text.primary}
        />
      </TableCell>
      <TableCell sx={{ minWidth: CLASSIFICATION_COLUMN_WIDTH }}>
        {row.companySpecificBusinessClassifications.length > 0
          ? row.companySpecificBusinessClassifications.map((item, index, defaultArray) => (
              <Box
                sx={{
                  height: `${getClassificationHeight(item.mainSkills)}px`,
                  maxWidth: CLASSIFICATION_COLUMN_WIDTH,
                  borderBottom: index !== defaultArray.length - 1 ? SUB_ROW_BORDER : "none",
                  paddingLeft: "4px",
                  // The box spans all of its main skills, so center the name vertically across them.
                  display: "flex",
                  alignItems: "center",
                }}
                key={item.id}
              >
                <EllipsisText
                  value={item.companySpecificBusinessClassificationName}
                  emptyText={EMPTY_STR.TEXT}
                  sx={{ ...SUB_ROW_TEXT_SX, minWidth: 0 }}
                  color={item.isHidden ? text.tertiary : text.primary}
                />
              </Box>
            ))
          : EMPTY_STR.TEXT}
      </TableCell>
      <MainSkillCell classifications={row.companySpecificBusinessClassifications} width="160px" getValue={(mainSkill) => mainSkill.mainSkillName} />
      <TableCell sx={{ minWidth: SECTOR_COUNCIL_COLUMN_WIDTH, paddingLeft: "8px !important" }}>
        <EllipsisText
          value={row.sectorCouncil}
          emptyText={EMPTY_STR.TEXT}
          sx={{ maxWidth: SECTOR_COUNCIL_COLUMN_WIDTH }}
          color={row.isHidden ? text.tertiary : text.primary}
        />
      </TableCell>
      <MainSkillCell classifications={row.companySpecificBusinessClassifications} width="210px" getValue={(mainSkill) => mainSkill.examName1Year} />
      <MainSkillCell
        classifications={row.companySpecificBusinessClassifications}
        width="160px"
        getValue={(mainSkill) => (mainSkill.examGrade1Year && EXAM_GRADE_1YEAR_LABEL[mainSkill.examGrade1Year]) || ""}
      />
      <MainSkillCell classifications={row.companySpecificBusinessClassifications} width="250px" getValue={(mainSkill) => mainSkill.examNameFinal} />
      <MainSkillCell
        classifications={row.companySpecificBusinessClassifications}
        width="190px"
        getValue={(mainSkill) => (mainSkill.examGradeFinal && EXAM_GRADE_FINAL_LABEL[mainSkill.examGradeFinal]) || ""}
      />
      <MainSkillCell
        classifications={row.companySpecificBusinessClassifications}
        width="200px"
        getValue={(mainSkill) =>
          (mainSkill.employerChangeRestrictionPeriod && EMPLOYER_CHANGE_RESTRICTION_PERIOD_LABEL[mainSkill.employerChangeRestrictionPeriod]) || ""
        }
      />
      <TableCell sx={{ minWidth: "150px" }}>
        {row.companySpecificBusinessClassifications.length > 0
          ? row.companySpecificBusinessClassifications.map((item, index, defaultArray) => (
              <Box
                sx={{
                  height: `${getClassificationHeight(item.mainSkills)}px`,
                  borderBottom: index !== defaultArray.length - 1 ? SUB_ROW_BORDER : "none",
                  paddingLeft: "4px",
                  // The box spans all of its main skills, so center the date vertically across them.
                  display: "flex",
                  alignItems: "center",
                }}
                key={item.id}
              >
                <Typography component="p" color={item.isHidden ? text.tertiary : text.primary} sx={{ ...SUB_ROW_TEXT_SX, px: 0.5 }} variant="body14">
                  {getLatestUpdatedAt(item) ? dayjs(getLatestUpdatedAt(item)).format(DATE_TIME_FORMAT) : EMPTY_STR.DATE}
                </Typography>
              </Box>
            ))
          : EMPTY_STR.TEXT}
      </TableCell>
      <TableCell
        sx={{
          textAlign: "center !important",
          minWidth: "120px",
          position: "sticky",
          right: "100px",
          bgcolor: background.white,
        }}
      >
        <Switch
          sx={{
            marginRight: 0,
            marginLeft: 0,
          }}
          checked={!row.isHidden}
          onChange={() => onChangeHidden(row)}
          color="primary"
        />
      </TableCell>
      <TableCell
        sx={{
          minWidth: "100px",
          textAlign: "center !important",
          paddingX: "4px !important",
          position: "sticky",
          right: "0px",
          bgcolor: background.white,
        }}
      >
        <Button size="sm" onClick={() => onEdit(row)}>
          編集
        </Button>
      </TableCell>
    </TableRow>
  );
};

export const CompanySpecificIndustryRow = memo(CompanySpecificIndustryRowBase, areRowPropsEqual);
